import { useEffect, useMemo, useState } from "react";
import { Box, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { administrationRouteApi } from "api/immunization";
import MasterModalShell from "./common/MasterModalShell";
import CodeField from "./common/CodeField";
import ExternalCodesField from "./common/ExternalCodesField";
import {
  validateCode,
  validateExternalCodes,
  validateRequired,
} from "./common/validation";

const AdministrationRouteModal = ({ open, onClose, data }) => {
  const recordId = data?._id;
  const isUpdate = Boolean(recordId);

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      code: data?.code || "",
      display: data?.display || "",
      description: data?.description || "",
      externalCodes:
        Array.isArray(data?.externalCodes) ? data.externalCodes : [],
      sequence: data?.sequence ?? 0,
    });
  }, [open, data]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const validationError = useMemo(
    () =>
      validateCode(form.code, { isUpdate, label: "Route code" }) ||
      validateRequired(form.display, "Display name") ||
      validateExternalCodes(form.externalCodes),
    [form, isUpdate],
  );

  const handleSubmit = async () => {
    if (validationError) return;
    setSaving(true);

    const payload = {
      display: form.display.trim(),
      description: form.description?.trim() || "",
      externalCodes: form.externalCodes,
      sequence: Number(form.sequence) || 0,
    };

    try {
      if (isUpdate) {
        await administrationRouteApi.update(recordId, payload);
        toast.success("Administration route updated successfully");
      } else {
        await administrationRouteApi.create({
          ...payload,
          code: form.code.trim(),
        });
        toast.success("Administration route created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} administration route`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterModalShell
      open={open}
      onClose={onClose}
      isUpdate={isUpdate}
      entityLabel="Administration Route"
      saving={saving}
      validationError={validationError}
      onSubmit={handleSubmit}
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        <CodeField
          value={form.code}
          onChange={(value) => set({ code: value })}
          isUpdate={isUpdate}
          label="Route Code"
          helperText='e.g. "intramuscular" becomes INTRAMUSCULAR'
        />
        <TextField
          label="Display Name"
          required
          fullWidth
          size="small"
          value={form.display || ""}
          onChange={(event) => set({ display: event.target.value })}
        />
        <TextField
          label="Sequence"
          type="number"
          size="small"
          sx={{ minWidth: 120 }}
          value={form.sequence}
          onChange={(event) => set({ sequence: event.target.value })}
        />
      </Box>

      <TextField
        label="Description"
        fullWidth
        size="small"
        multiline
        minRows={2}
        value={form.description || ""}
        onChange={(event) => set({ description: event.target.value })}
      />

      <ExternalCodesField
        value={form.externalCodes}
        onChange={(next) => set({ externalCodes: next })}
        disabled={saving}
      />
    </MasterModalShell>
  );
};

export default AdministrationRouteModal;
